import {styled} from '@mui/material/styles'
import TablePagination from '@mui/material/TablePagination'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import {Search} from '@mui/icons-material'

const resolveStyleObject = (styleInput, theme) => {
  if (typeof styleInput === 'function') return styleInput(theme)
  if (styleInput && typeof styleInput === 'object') return styleInput
  return {}
}

const StyledTableCellBase = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'tableVariant' && prop !== 'tableType'
})(({tableVariant, tableType, theme}) => ({
  ...(tableType === 'compact' && {padding: '6px 12px'}),
  ...(tableType === 'default' && {padding: '10px 14px'}),
  ...(tableVariant === 'ghost' && {
    borderBottom: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.hpBaseDarker
  })
}))

export const StyledTableWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

export const StyledSearchAddButtonBox = styled(Box)(({theme}) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius
}))

export const StyledSearchTextField = styled(TextField)(() => ({
  width: 340,
  '& .MuiOutlinedInput-root': {
    height: 40
  }
}))

export const StyledSearchIcon = styled(Search)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize
}))

export const StyledAddButton = styled(Button)(() => ({
  textTransform: 'none',
  height: 40
}))

export const StyledLoadingBox = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4)
}))

export const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== 'tableContainerStyles' && prop !== 'tableVariant'
})(({tableContainerStyles, tableVariant, theme}) => ({
  ...(tableVariant === 'ghost' && {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    color: theme.palette.hpBaseDarker
  }),
  ...resolveStyleObject(tableContainerStyles, theme)
}))

export const StyledBodyTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'rowStyles' && prop !== 'isInteractive'
})(({rowStyles, isInteractive, theme}) => ({
  ...resolveStyleObject(rowStyles, theme),
  ...(isInteractive && {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '-2px'
    }
  })
}))

export const StyledHeaderTableCell = styled(StyledTableCellBase, {
  shouldForwardProp: (prop) => prop !== 'headerStyles'
})(({headerStyles, theme}) => ({
  fontWeight: theme.typography.fontWeightSemibold,
  ...resolveStyleObject(headerStyles, theme)
}))

export const StyledBodyTableCell = styled(StyledTableCellBase, {
  shouldForwardProp: (prop) => prop !== 'cellStyles'
})(({cellStyles, theme}) => ({
  ...resolveStyleObject(cellStyles, theme)
}))

export const StyledTablePagination = styled(TablePagination, {
  shouldForwardProp: (prop) => prop !== 'tableVariant'
})(({tableVariant, theme}) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  ...(tableVariant === 'ghost' && {
    borderTop: 'none'
  }),
  '& .MuiTablePagination-toolbar': {
    minHeight: 48,
    padding: '0 16px'
  },
  '& .MuiTablePagination-spacer': {
    display: 'none'
  },
  '& .MuiTablePagination-selectLabel': {
    fontSize: theme.typography.body2.fontSize,
    color: tableVariant === 'ghost' ? theme.palette.hpBaseDarker : theme.palette.text.primary
  },
  '& .MuiTablePagination-select': {
    fontSize: theme.typography.body2.fontSize,
    color: tableVariant === 'ghost' ? theme.palette.hpBaseDarker : theme.palette.text.primary,
    border: tableVariant === 'ghost' ? 'none' : `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: '4px 24px 4px 8px'
  },
  '& .MuiTablePagination-displayedRows': {
    fontSize: theme.typography.body2.fontSize,
    color: tableVariant === 'ghost' ? theme.palette.hpBaseDarker : theme.palette.text.primary,
    marginLeft: 'auto'
  },
  '& .MuiTablePagination-actions': {
    marginLeft: 16,
    '& .MuiIconButton-root': {
      color: tableVariant === 'ghost' ? theme.palette.hpBaseDarker : theme.palette.text.secondary,
      padding: 3,
      border: tableVariant === 'ghost' ? 'none' : `1px solid ${theme.palette.divider}`,
      borderRadius: '100%',
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '&:not(:first-child)': {
        marginLeft: 5
      }
    },
    '& .Mui-disabled': {
      color: theme.palette.action.disabled
    }
  }
}))
